// Expenses (finance/ledger target, TD-041, TD-075, TD-077): the Add expense and template forms, how a bill reads by its
// months and cadence, and how client ad spend reads (TD-088). Pure so the rules can be tested. The bill rule is the
// server's (expense_rules.bill_state).
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { formatMoney } from "@/utils/currency";
import type {
  AdSpendFlow, DeletedExpenseRecord, EntryCadence, ExpenseCadence, ExpenseCategory, ExpenseFlow, ExpenseTemplate, ExpenseTemplatesResponse,
  NewTemplateInput, RecordExpenseInput, UpdateTemplateInput,
} from "../types";
import { dateLabel, dayMonthLabel, monthLabel, shortMonthLabel } from "./format";
import { passCheck, passForm, passInput, type PassForm } from "./passedCosts";
import { dateError, daysBefore, PAST_DAYS_MAX } from "./jobForms";

const clientWord = ENTITY_CONFIG.client.label.toLowerCase();
const expenseWord = ENTITY_CONFIG.expense.label.toLowerCase();

export const CADENCE_LABELS: Record<EntryCadence, string> = {
  once: "Once",
  monthly: "Monthly",
  quarterly: "Every 3 months, spread 1/3",
  yearly: "Yearly, spread 1/12",
};

/**
 * "Jul 2026 – Jun 2027" for a bill counting in several months, else its month: the span the server says it takes once
 * paid (`countsIn`), never derived here. A varying bill not paid yet has one placeholder line, but its span is already
 * the months the payment will book: a yearly one's twelve, folded into its stopped expense's stop month.
 */
export function monthsText(e: Pick<ExpenseFlow, "countsIn" | "accrualMonth">): string {
  const span = e.countsIn;
  if (span && span.from !== span.to) return `${shortMonthLabel(span.from)} – ${shortMonthLabel(span.to)}`;
  // One month: the bill's own (a span always starts at its accrual month).
  return monthLabel(e.accrualMonth);
}

/** The Money row's For column: "Software · October 2026", and "· passed along" when the client is billed for it (TD-100). */
export const entryFor = (e: ExpenseFlow): string => `${e.categoryLabel} · ${monthsText(e)}${e.passAlong ? " · passed along" : ""}`;

/** FACEBOOK → Facebook, GOOGLE_SEARCH → Google search. */
export const platformName = (platform: string): string => {
  const words = platform.toLowerCase().replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * What a month of ad spend's modal says happened (TD-088). A month a correction left below zero is money back from the
 * platform: the rebill takes ad spend net, so it lowers what the client's next invoice adds at cost.
 */
export function adSpendNote(a: Pick<AdSpendFlow, "amountMinor" | "month">, today: string): string {
  if (a.amountMinor < 0) {
    return `More came back to the agency's ad account than it spent: money back from the platform. It lowers the ads at cost the ${clientWord}'s invoices add.`;
  }
  const soFar = a.month === today.slice(0, 7) ? ", so far this month" : "";
  return `Paid by the agency's ad account as it was spent${soFar}. The ${clientWord} pays ad spend back at cost on their invoices.`;
}

/**
 * The client's ad spend still fronted, as of today. A rebill is spend to date less what was rebilled, not a month's, so
 * no month names its invoice: this is the client's total, read as today's.
 */
export function frontedLine(clientName: string, frontedMinor: number): string {
  if (frontedMinor <= 0) return `Today, none of ${clientName}'s ad spend is fronted: every invoice has rebilled it.`;
  return `Today, ${formatMoney(frontedMinor, "EUR")} of ${clientName}'s ad spend is fronted and not invoiced yet.`;
}

/** What a month of the agency's own ad spend's modal says (TD-105). */
export const OWN_ADS_NOTE = "The agency's own ad spend: an expense its ad account paid, never fronted for a client or rebilled.";

/** 1st, 2nd, 3rd, 4th ... 11th, 12th, 13th ... 21st, 22nd. */
export function dayLabel(day: number): string {
  const teen = day % 100 >= 11 && day % 100 <= 13;
  const suffix = teen ? "th" : day % 10 === 1 ? "st" : day % 10 === 2 ? "nd" : day % 10 === 3 ? "rd" : "th";
  return `${day}${suffix}`;
}

/**
 * "Monthly, on the 5th"; "Every 3 months from 10 Jul, spread 1/3"; "Yearly on 10 Jul, spread 1/12". A stopped quarterly
 * or yearly expense folds the rest of its cost into the stop month, so it says it stopped and where the rest counts.
 */
export function cadenceText(t: Pick<ExpenseTemplate, "cadence" | "day" | "startsOn" | "endsOn">): string {
  if (t.cadence === "monthly") return `Monthly, on the ${dayLabel(t.day)}`;
  const on = dayMonthLabel(t.startsOn);
  const [repeats, spread] = t.cadence === "quarterly" ? [`Every 3 months from ${on}`, "spread 1/3"] : [`Yearly on ${on}`, "spread 1/12"];
  return t.endsOn ? `${repeats}, stopped: the rest counts in ${monthLabel(t.endsOn.slice(0, 7))}` : `${repeats}, ${spread}`;
}

const countsInOneMonth = (e: Pick<ExpenseFlow, "countsIn">): boolean => !!e.countsIn && e.countsIn.from === e.countsIn.to;

const SPREAD_NAMES: Partial<Record<EntryCadence, { name: string; part: string }>> = {
  quarterly: { name: "Every 3 months", part: "1/3" },
  yearly: { name: "Yearly", part: "1/12" },
};

/** A bill's Repeats row: a quarterly or yearly bill of a stopped expense counts in one month, so it is not "spread". */
export const entryCadenceText = (e: Pick<ExpenseFlow, "cadence" | "countsIn" | "accrualMonth">): string => {
  const spread = SPREAD_NAMES[e.cadence];
  return spread && countsInOneMonth(e) ? `${spread.name}, stopped: counts in ${monthsText(e)}` : CADENCE_LABELS[e.cadence];
};

/** The note on a quarterly or yearly bill's Mark paid: the cash leaves once; the cost counts a part a month, or all in one month once stopped. */
export const spreadBillNote = (e: Pick<ExpenseFlow, "cadence" | "countsIn" | "accrualMonth">): string =>
  countsInOneMonth(e)
    ? `The cash leaves once, on the day it is paid; the ${expenseWord} stopped, so the whole cost counts in ${monthsText(e)}.`
    : `The cash leaves once, on the day it is paid; the cost counts ${SPREAD_NAMES[e.cadence]?.part ?? "1/12"} a month, ${monthsText(e)}.`;

// ---- forms

export type Repeats = "once" | ExpenseCadence;

export const REPEAT_OPTIONS: { value: Repeats; label: string }[] = [
  { value: "once", label: "Once, already paid" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Every 3 months, spread 1/3" },
  { value: "yearly", label: "Yearly, spread 1/12" },
];

export const toRepeats = (value: string): Repeats => (value === "once" || value === "quarterly" || value === "yearly" ? value : "monthly");

/** The category picker: an existing category, or a new one named here (`id` null). */
export interface CategoryChoice {
  id: string | null;
  newLabel: string;
}

/** The category select's value that opens the new category field. */
export const NEW_CATEGORY = "__new";

export interface ExpenseForm {
  label: string;
  amount: number | null;
  /** Repeating only: each bill's amount is entered when it is paid. */
  varies: boolean;
  repeats: Repeats;
  /** Once: the day it was paid. Repeating: the first bill's date. */
  date: string;
  category: CategoryChoice;
  /** "" for overhead. */
  clientId: string;
  /** "" for none. */
  serviceLineId: string;
  pass: PassForm;
  pending: boolean;
}

export interface ExpenseCheck {
  labelError: string | undefined;
  amountError: string | undefined;
  dateError: string | undefined;
  categoryError: string | undefined;
  markupError: string | undefined;
  blocked: boolean;
}

const categoryError = (c: CategoryChoice): string | undefined => (c.id || c.newLabel.trim() ? undefined : "Pick a category or name a new one");

/**
 * What an expense form still lacks, in form order, for the line above its save (DD-056). A varying amount is entered per bill, so it is
 * not missing. `date` is the add form's day, left out by a form that has none.
 */
export const expenseMissing = (form: { label: string; amount: number | null; varies: boolean; category: CategoryChoice; date?: string }, once: boolean): string[] => [
  ...(form.label.trim() ? [] : ["what it is"]),
  ...(form.date === "" ? [once ? "the payment date" : "the first bill's date"] : []),
  ...((once || !form.varies) && !form.amount ? ["the amount"] : []),
  ...(categoryError(form.category) ? ["a category"] : []),
];

/** The last day a bill can repeat on (expense_rules.DAY_MAX): every month has it. */
const DAY_MAX = 28;

/** The same day next year, 29 February giving 28 February (expense_service._a_year_after). */
export function aYearAfter(iso: string): string {
  const monthDay = iso.slice(5) === "02-29" ? "02-28" : iso.slice(5);
  return `${Number(iso.slice(0, 4)) + 1}-${monthDay}`;
}

/**
 * A repeating expense's first bill, as the server takes it: a day 1 to 28, no more than two years back, and at most a
 * year ahead, so a typo'd year never bills (expense_service.FIRST_BILL_TOO_FAR, the server's words).
 */
function firstBillError(date: string, today: string): string | undefined {
  if (!date) return "Enter the first bill's date";
  if (Number(date.slice(8, 10)) > DAY_MAX) return "Bills repeat on days 1 to 28: pick one of those";
  if (date < daysBefore(today, PAST_DAYS_MAX)) return "The first bill is more than two years ago";
  return date > aYearAfter(today) ? "The first bill can be at most a year ahead." : undefined;
}

/** The date Add expense opens with: today for a bill paid once; for a repeating one today, or the 28th from the 29th on. */
export const firstDate = (repeats: Repeats, today: string): string =>
  repeats === "once" || Number(today.slice(8, 10)) <= DAY_MAX ? today : `${today.slice(0, 8)}${DAY_MAX}`;

/** The date after Repeats changes: still at the old default, it moves to the new one; a date the user picked stays. */
export const dateOnRepeat = (date: string, from: Repeats, to: Repeats, today: string): string =>
  date === firstDate(from, today) ? firstDate(to, today) : date;

/** The pick, unless that category was deleted meanwhile: then none, so the form asks for another. */
export const liveCategory = (c: CategoryChoice, categories: ExpenseCategory[]): CategoryChoice =>
  c.id && !categories.some((k) => k.id === c.id) ? { id: null, newLabel: "" } : c;

export function expenseCheck(form: ExpenseForm, today: string): ExpenseCheck {
  const once = form.repeats === "once";
  const labelError = form.label.trim() ? undefined : "Required";
  const amountError = (once || !form.varies) && (!form.amount || form.amount <= 0) ? "Enter the amount" : undefined;
  const dateErr = once ? dateError(form.date, today, "payment") : firstBillError(form.date, today);
  const catError = categoryError(form.category);
  const pass = passCheck(form.pass, form.clientId);
  return {
    labelError, amountError, dateError: dateErr, categoryError: catError, markupError: pass.markupError,
    blocked: !!labelError || !!amountError || !!dateErr || !!catError || pass.blocked || form.pending,
  };
}

const categoryOf = (c: CategoryChoice): { categoryId: string } | { categoryLabel: string } =>
  c.id ? { categoryId: c.id } : { categoryLabel: c.newLabel.trim() };

export type ExpenseRequest = { kind: "once"; input: RecordExpenseInput } | { kind: "template"; input: NewTemplateInput };

export function expenseRequest(form: ExpenseForm): ExpenseRequest {
  const shared = {
    label: form.label.trim(), clientId: form.clientId || null, serviceLineId: form.serviceLineId || null, ...categoryOf(form.category),
    ...passInput(form.pass, form.clientId),
  };
  if (form.repeats === "once") return { kind: "once", input: { ...shared, amountMinor: form.amount ?? 0, paidOn: form.date } };
  return { kind: "template", input: { ...shared, amountMinor: form.varies ? null : form.amount, cadence: form.repeats, startsOn: form.date } };
}

export interface TemplateForm {
  label: string;
  amount: number | null;
  varies: boolean;
  day: number;
  category: CategoryChoice;
  clientId: string;
  serviceLineId: string;
  /** Not edited here (API only for now), but sent back as it is: the server sets every dimension a save names or omits. */
  platform: string | null;
  pass: PassForm;
  pending: boolean;
}

export const templateForm = (t: ExpenseTemplate): Omit<TemplateForm, "pending"> => ({
  label: t.label,
  amount: t.amountMinor,
  varies: t.amountMinor === null,
  day: t.day,
  category: { id: t.categoryId, newLabel: "" },
  clientId: t.clientId ?? "",
  serviceLineId: t.serviceLineId ?? "",
  platform: t.platform,
  pass: passForm(t.passAlong),
});

export function templateCheck(
  form: TemplateForm,
): { labelError?: string; amountError?: string; dayError?: string; categoryError?: string; markupError?: string; blocked: boolean } {
  const labelError = form.label.trim() ? undefined : "Required";
  const amountError = !form.varies && (!form.amount || form.amount <= 0) ? "Enter the amount, or tick that it varies" : undefined;
  const dayError = Number.isInteger(form.day) && form.day >= 1 && form.day <= 28 ? undefined : "1 to 28";
  const catError = categoryError(form.category);
  const pass = passCheck(form.pass, form.clientId);
  return {
    labelError, amountError, dayError, categoryError: catError, markupError: pass.markupError,
    blocked: !!labelError || !!amountError || !!dayError || !!catError || pass.blocked || form.pending,
  };
}

export const templateInput = (form: TemplateForm): UpdateTemplateInput => ({
  label: form.label.trim(),
  amountMinor: form.varies ? null : form.amount,
  day: form.day,
  clientId: form.clientId || null,
  serviceLineId: form.serviceLineId || null,
  platform: form.platform,
  ...categoryOf(form.category),
  ...passInput(form.pass, form.clientId),
});

/** The templates list with one template as the server just answered it, so a reopened modal reads it at once. */
export const replaceTemplate = (list: ExpenseTemplatesResponse, t: ExpenseTemplate): ExpenseTemplatesResponse => ({
  ...list,
  templates: list.templates.map((row) => (row.id === t.id ? t : row)),
});

export interface BillForm {
  amount: number | null;
  paidOn: string;
  pending: boolean;
}

export function billCheck(form: BillForm, today: string): { amountError: string | undefined; dateError: string | undefined; blocked: boolean } {
  const amountError = !form.amount || form.amount <= 0 ? "Enter the amount on the bill" : undefined;
  const paidOnError = dateError(form.paidOn, today, "payment");
  return { amountError, dateError: paidOnError, blocked: !!amountError || !!paidOnError || form.pending };
}

export const stopToast = (t: ExpenseTemplate): string => `${t.label} stops after ${t.endsOn ? monthLabel(t.endsOn.slice(0, 7)) : "this month"}.`;

// ---- taking an expense or a category back (TD-099): only while nothing paid or names it

/** A step the expense's modal confirms in its footer (DD-045). */
export type TemplateStep = "stop" | "delete";

/** The row menu's actions: Stop while it runs, Delete while none of its bills was paid. */
export function templateActions(t: Pick<ExpenseTemplate, "endsOn" | "deletable">): TemplateStep[] {
  const actions: TemplateStep[] = t.endsOn ? [] : ["stop"];
  return t.deletable ? actions.concat("delete") : actions;
}

/** The step the modal opens at: the one the row menu put in the URL, while the expense still allows it. */
export function startStep(param: string | null, t: Pick<ExpenseTemplate, "endsOn" | "deletable">): TemplateStep | null {
  return templateActions(t).find((step) => step === param) ?? null;
}

export const deleteConfirm = (label: string): string => `Delete ${label}? None of its bills was paid, so they go with it.`;

export const deletedToast = (r: DeletedExpenseRecord): string => `${r.label} deleted.`;

export const categoryUse = (c: Pick<ExpenseCategory, "inUse">): string => (c.inUse ? "In use" : "Not used");

export function addedToast(t: ExpenseTemplate): string {
  if (t.unpaid > 0) return `${t.label} added: ${t.unpaid} bill${t.unpaid === 1 ? "" : "s"} to mark paid in Money.`;
  return `${t.label} added: first bill ${dateLabel(t.nextBill)}.`;
}
